import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Card, CardBody } from 'reactstrap';
import { type Person, type ReduxState } from 'types';
import { parseAndFormatQuestionResponse } from 'utils/models/Performance';
import { stripHtml } from 'utils/util/util';
import ThreadedCommentButton from 'views/Widgets/Comments/ThreadedCommentButton';
import { useThreadedCommentsByObjectId } from 'views/Widgets/Comments/ThreadedCommentsByObjectIdContext';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import Loading from 'views/Widgets/Loading';
import Avatar from 'views/Widgets/People/Avatar';
import {
  extractSurveyAnswerResponse,
  noAnswerElement,
  type SurveyQuestionDefinition,
} from './formatSurveyAnswer';

interface SurveyQuestion {
  id: number;
  order: number;
  required: boolean;
  question: SurveyQuestionDefinition;
  answer: {
    id: number;
    answer: Record<string, unknown>;
    can_comment: boolean;
  } | null;
}

export interface SurveyParticipation {
  id: number;
  survey_instance: number;
  survey_name: string;
  survey_type: string;
  questions: SurveyQuestion[];
  has_answers: boolean;
  is_expired: boolean;
  created_at: string;
}

interface Props {
  participation: SurveyParticipation | undefined;
  respondent?: Person;
  // Whether to show the per-answer comment icon/composer. Survey answers
  // don't support commenting on the self-service "Surveys & performance"
  // page, only on the admin participant response views.
  showComments?: boolean;
}

const CustomSurveyResponsesViewer: FC<Props> = ({
  participation,
  respondent,
  showComments = true,
}) => {
  const { formatMessage, locale } = useIntl();
  const customSurveyAnswerContentType = useSelector(
    (state: ReduxState) => state.attachedContentTypes.custom_survey_answer
  );
  const {
    commentsByObjectId,
    isLoading: commentsLoading,
    onCommentsChange: onAnswerCommentsChange,
  } = useThreadedCommentsByObjectId();

  if (!participation || commentsLoading) {
    return <Loading />;
  }

  return (
    <>
      {participation.questions.map((q) => {
        const answer = q.answer;
        const plainQuestionTitle = stripHtml(q.question.label).trim();
        return (
          <Card key={q.id} className="mb-3">
            <CardBody>
              <h4 className="mb-3">
                <RichTextViewer model={q.question.label} expanded={true} />
              </h4>
              <div className="d-flex align-items-center">
                {respondent && (
                  <Avatar
                    person={respondent}
                    size="sm"
                    className="me-3 flex-shrink-0"
                  />
                )}
                <div className="fr-view flex-grow-1">
                  {parseAndFormatQuestionResponse({
                    q: {
                      question: q.question,
                      response: extractSurveyAnswerResponse(
                        q.question.type,
                        answer?.answer
                      ),
                    },
                    noneElement: noAnswerElement,
                    formatMessage,
                    locale,
                  })}
                </div>
                {answer && showComments && (
                  <ThreadedCommentButton
                    contentType={customSurveyAnswerContentType}
                    objectId={answer.id}
                    canComment={answer.can_comment}
                    comments={commentsByObjectId?.[answer.id] ?? []}
                    onCommentsChange={(comments) =>
                      onAnswerCommentsChange(answer.id, comments)
                    }
                    title={formatMessage(
                      {
                        id: 'app.views.custom_surveys.survey_response_view.comments_modal.title',
                        defaultMessage: 'Comments for {person} - “{question}”',
                      },
                      {
                        person:
                          respondent?.full_name ??
                          formatMessage({
                            id: 'app.views.custom_surveys.survey_response_view.comments_modal.person_placeholder',
                            defaultMessage: 'this person',
                          }),
                        question:
                          plainQuestionTitle ||
                          formatMessage({
                            id: 'app.views.custom_surveys.survey_response_view.comments_modal.question_placeholder',
                            defaultMessage: 'this question',
                          }),
                      }
                    )}
                    addCommentLabel={
                      <FormattedMessage
                        id="app.views.custom_surveys.survey_response_view.comments_modal.add_comment_label"
                        defaultMessage="Add a comment"
                      />
                    }
                  />
                )}
              </div>
            </CardBody>
          </Card>
        );
      })}
      {participation.questions.length === 0 && (
        <Card>
          <CardBody className="text-muted">
            <FormattedMessage
              id="app.views.custom_surveys.survey_response_view.no_questions"
              defaultMessage="No questions available for this survey."
            />
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default CustomSurveyResponsesViewer;
